// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (src/components/chartGeometry.js).
//
// Geometry helpers that tie the d3-sankey layout to the CANVAS box (see
// sankeyTokens.js).
//
// 1. Node positions (pure, before render): d3-sankey's own node ordering is a
//    crossing-minimisation heuristic, so buckets would not come out in legend
//    order. Each node's y0/y1 is computed here instead, and the column x
//    positions are computed here too, then pinned on the d3-sankey nodes.
// 2. Minimum sizes: tiny nodes and ribbons are drawn at a minimum height so they
//    stay visible and clickable (computeSankeyGeometry).

import { CANVAS, PLOT_H, SPACING, VIEW_W, HOST_BOX } from "./sankeyTokens";

/** Total vertical gap reserved in a column with `bucketCount` slots. */
export function totalGap(bucketCount) {
  return CANVAS.gap * Math.max(0, bucketCount - 1);
}

/** Canvas units per entity. */
export function entityScale(total, bucketCount) {
  return total > 0 ? (PLOT_H - totalGap(bucketCount)) / total : 0;
}

/**
 * Which buckets get a slot: those with a count in at least one column. A bucket that
 * is empty in every column would otherwise reserve a slot plus a gap and push the
 * other buckets down, leaving blank space at the top of the plot.
 *
 * @param {number[][]} distributions  distributions[columnIndex][bucketIndex] = count
 * @returns {boolean[]}  usedBuckets[bucketIndex]
 */
export function getUsedBuckets(distributions) {
  const bucketCount = distributions[0]?.length ?? 0;
  return Array.from({ length: bucketCount }, (_, b) => distributions.some((dist) => dist[b] > 0));
}

/** Number of buckets that get a slot (see getUsedBuckets). Pass to entityScale. */
export function usedBucketCount(distributions) {
  return getUsedBuckets(distributions).filter(Boolean).length;
}

/**
 * Minimum sizes, in canvas (= viewBox) units. Vertically one unit is one px (the box
 * has a fixed height, see CANVAS_UNITS_PER_PX_Y), so these are px:
 *   MIN_NODE_H    10px: a bucket with a handful of items stays visible and easy to
 *                 click. 10 buckets at this size plus 9 gaps (~244px) fit PLOT_H.
 *   MIN_RIBBON_H   3px: the thinnest ribbon is still visible.
 *   RIBBON_HIT_H   8px: the thinnest ribbon's hover / click target (drawn as a
 *                 transparent stroke, so the ribbon looks the same).
 */
export const MIN_NODE_H = 10;
export const MIN_RIBBON_H = 3;
export const RIBBON_HIT_H = 8;

const ribbonKey = (fromIndex, fromBucket, toBucket) => `${fromIndex}:${fromBucket}-${toBucket}`;

/**
 * Node and ribbon positions for the whole chart (viewBox units, 0 = plot top).
 *
 *   - Buckets keep legend order top to bottom in every column; a bucket used in some
 *     column keeps a slot (at least CANVAS.minSlotH) in every column, separated by
 *     CANVAS.gap; a bucket empty in every column gets no slot.
 *   - A ribbon is max(value * scale, MIN_RIBBON_H) thick.
 *   - A node is at least MIN_NODE_H tall, and at least as tall as the ribbons leaving
 *     it and the ribbons arriving at it, so every ribbon end lies inside its node.
 *   - Ribbons stack top to bottom at the source node in (source, target) bucket order
 *     and independently at the target node in (target, source) order.
 *   - The proportional scale is the largest that still fits every column in PLOT_H with
 *     the minimum sizes; only if the minimums alone overflow is everything shrunk
 *     uniformly to fit (fits: false).
 *
 * @param {number[][]} distributions  distributions[columnIndex][bucketIndex] = count
 * @param {{id?:string, fromIndex:number, fromBucket:number, toBucket:number, value:number}[]} [flows]
 * @returns {{ absY: {y0:number,y1:number}[][], ribbons: Map<string,{yL0:number,yL1:number,yR0:number,yR1:number}>, scale: number, fits: boolean }}
 *          ribbons are keyed by flow.id when given, else by `${fromIndex}:${fromBucket}-${toBucket}`
 */
export function computeSankeyGeometry(distributions, flows = []) {
  const dists = Array.isArray(distributions) ? distributions : [];
  const flowList = (Array.isArray(flows) ? flows : []).filter(
    (f) => dists[f?.fromIndex] && dists[f.fromIndex + 1] && f.value > 0
  );
  const usedBuckets = getUsedBuckets(dists);
  const used = usedBuckets.filter(Boolean).length;
  const total = Math.max(0, ...dists.map((d) => d.reduce((a, b) => a + b, 0)));
  const maxScale = entityScale(total, used);

  const thickness = (f, scale) => Math.max(f.value * scale, MIN_RIBBON_H);

  const nodeHeights = (scale) => {
    const outSum = dists.map((d) => d.map(() => 0));
    const inSum = dists.map((d) => d.map(() => 0));
    flowList.forEach((f) => {
      const t = thickness(f, scale);
      outSum[f.fromIndex][f.fromBucket] += t;
      inSum[f.fromIndex + 1][f.toBucket] += t;
    });
    return dists.map((d, c) => d.map((count, b) => (
      count > 0 ? Math.max(count * scale, MIN_NODE_H, outSum[c][b], inSum[c][b]) : 0
    )));
  };

  const tallestColumn = (heights) => Math.max(0, ...heights.map((col) => (
    col.reduce((sum, h, b) => (usedBuckets[b] ? sum + Math.max(h, CANVAS.minSlotH) : sum), 0) + totalGap(used)
  )));

  // Largest scale whose layout fits (column height grows with the scale).
  let scale = maxScale;
  if (tallestColumn(nodeHeights(scale)) > PLOT_H) {
    let lo = 0;
    let hi = maxScale;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (tallestColumn(nodeHeights(mid)) > PLOT_H) hi = mid;
      else lo = mid;
    }
    scale = lo;
  }
  const heights = nodeHeights(scale);
  const tallest = tallestColumn(heights);
  const fits = tallest <= PLOT_H + 1e-6;
  const k = fits || tallest === 0 ? 1 : PLOT_H / tallest; // last resort: shrink uniformly

  const absY = heights.map((col) => {
    let cursor = 0;
    return col.map((h, b) => {
      if (!usedBuckets[b]) return { y0: cursor * k, y1: cursor * k };
      const y0 = cursor;
      cursor += Math.max(h, CANVAS.minSlotH) + CANVAS.gap;
      return { y0: y0 * k, y1: (y0 + h) * k };
    });
  });

  const ribbons = new Map();
  const leftCursor = absY.map((col) => col.map((a) => a.y0));
  const rightCursor = absY.map((col) => col.map((a) => a.y0));
  const keyOf = (f) => f.id ?? ribbonKey(f.fromIndex, f.fromBucket, f.toBucket);
  [...flowList]
    .sort((a, b) => a.fromIndex - b.fromIndex || a.fromBucket - b.fromBucket || a.toBucket - b.toBucket)
    .forEach((f) => {
      const t = thickness(f, scale) * k;
      const yL0 = leftCursor[f.fromIndex][f.fromBucket];
      leftCursor[f.fromIndex][f.fromBucket] += t;
      ribbons.set(keyOf(f), { yL0, yL1: yL0 + t });
    });
  [...flowList]
    .sort((a, b) => a.fromIndex - b.fromIndex || a.toBucket - b.toBucket || a.fromBucket - b.fromBucket)
    .forEach((f) => {
      const t = thickness(f, scale) * k;
      const yR0 = rightCursor[f.fromIndex + 1][f.toBucket];
      rightCursor[f.fromIndex + 1][f.toBucket] += t;
      Object.assign(ribbons.get(keyOf(f)), { yR0, yR1: yR0 + t });
    });

  return { absY, ribbons, scale: scale * k, fits };
}

/**
 * Node slots only (no ribbons), for callers that just need y0/y1 per bucket.
 * Same rules as computeSankeyGeometry.
 *
 * @param {number[][]} distributions
 * @returns {{ y0: number, y1: number }[][]}  absY[columnIndex][bucketIndex]
 */
export function computeNodeAbsY(distributions) {
  return computeSankeyGeometry(distributions, []).absY;
}

/**
 * Left/right edge (viewBox units) of every column's node rects. Same result as
 * d3-sankey's sankeyLeft alignment over the [SPACING, VIEW_W + SPACING] extent, but
 * computed for every column, so a column with no incoming ribbons (or no nodes)
 * still lands in its own slot. A single column is centred.
 *
 * @param {number} columnCount
 * @param {number} nodeWidth
 * @returns {{ x0: number, x1: number }[]}
 */
export function computeColumnX(columnCount, nodeWidth) {
  const free = VIEW_W - nodeWidth;
  return Array.from({ length: Math.max(0, columnCount) }, (_, c) => {
    const x0 = SPACING + (columnCount > 1 ? (c * free) / (columnCount - 1) : free / 2);
    return { x0, x1: x0 + nodeWidth };
  });
}

/**
 * Label anchor of every column in canvas units (rect left + nodeLeftOfCentre),
 * for the month axis labels. The SVG host is HOST_BOX.width canvas units wide
 * with a viewBox of the same width, so viewBox x maps 1:1 onto canvas x.
 */
export function columnAnchorX(columnX) {
  return columnX.map((c) => HOST_BOX.left + c.x0 + CANVAS.nodeLeftOfCentre);
}

// ---- overlay coordinate conversion (canvas units -> % of the CANVAS box) ----

export const xToPercent = (x) => (x / CANVAS.W) * 100;
export const yToPercent = (y) => (y / CANVAS.H) * 100;
